const catalogCache = new WeakMap()
const CACHE_MS = 5 * 60 * 1000

export async function fetchAllPaintProducts(api) {
  const products = []
  const ids = new Set()
  let expectedTotal = null
  for (let page = 1; page <= 50; page++) {
    const response = await api.get('/liner/paint-products', { params: { page, limit: 1000 }, timeout: 15000 })
    if (Number(response.code) !== 200 || !Array.isArray(response.data?.list)) throw new Error('色卡加载失败，请重试')
    const { list, total } = response.data
    if (total !== undefined && (!Number.isInteger(total) || total < 0)) throw new Error('色卡数量异常，请重试')
    if (expectedTotal !== null && total !== expectedTotal) throw new Error('色库正在更新，请重新加载')
    if (total !== undefined) expectedTotal = total
    for (const product of list) {
      if (!product.id || ids.has(product.id)) throw new Error('色卡分页不完整，请重新加载')
      ids.add(product.id)
      products.push(product)
    }
    if (expectedTotal === null) {
      if (list.length >= 1000) throw new Error('色库需要更新后才能完整展示，请联系站长')
      return products // Compatibility with an older server and a small catalog.
    }
    if (products.length === expectedTotal) return products
    if (!list.length || products.length > expectedTotal) throw new Error('色卡分页不完整，请重新加载')
  }
  throw new Error('色卡加载未完成，请重试')
}

export function loadPaintCatalog(api, { force = false } = {}) {
  const cached = catalogCache.get(api)
  if (!force && cached && Date.now() - cached.created < CACHE_MS) return cached.promise
  const entry = { created: Date.now() }
  entry.promise = fetchAllPaintProducts(api).catch(error => {
    if (catalogCache.get(api) === entry) catalogCache.delete(api)
    throw error
  })
  catalogCache.set(api, entry)
  return entry.promise
}

export function paintBrands(products) {
  const counts = new Map()
  products.forEach(product => counts.set(product.brand, (counts.get(product.brand) || 0) + 1))
  return [...counts].map(([brand, count]) => ({ brand, count })).sort((a, b) => a.brand.localeCompare(b.brand, 'zh-CN'))
}

export function isMetallic(product) {
  return ['1', '是', 'true'].includes(String(product.is_metallic).toLowerCase())
}

export function paintEffect(product) {
  const description = [product.chinese_name, product.english_name, product.paint_type, product.gloss].join(' ').toLowerCase()
  if (/稀释|清洗|溶剂|工具|介质|消光剂|thinner|cleaner|flat base|transparent medium/.test(description)) return '辅助用品'
  if (/虹彩|珠光|iridescen|prismatic|pearl/.test(description)) return '珠光 / 虹彩'
  if (/透明|清漆|clear|transparent|varnish|sealer|糖果色|candy/.test(description)) return '透明色'
  if (/荧光|蛍光|fluorescen/.test(description)) return '荧光色'
  return isMetallic(product) || /金属|metallic/.test(description) ? '金属色' : '普通色'
}

export function glossLabel(value) {
  return ({ 光泽: '亮光', 哑光: '消光', '-': '未标注' })[value] || value || '未标注'
}

export function applicationMatches(product, method) {
  return !method || String(product.application_method || '').includes(method)
}

export function filterPaintProducts(products, filters) {
  const query = String(filters.query || '').trim().toLowerCase()
  return products.filter(product => {
    if (filters.brand && product.brand !== filters.brand) return false
    if (filters.series && product.series !== filters.series) return false
    if (filters.type && product.paint_type !== filters.type) return false
    if (filters.gloss && glossLabel(product.gloss) !== filters.gloss) return false
    if (filters.effect && paintEffect(product) !== filters.effect) return false
    if (!applicationMatches(product, filters.application)) return false
    return !query || [product.brand, product.color_code, product.chinese_name, product.english_name, product.hex_value].some(value => String(value || '').toLowerCase().includes(query))
  })
}

function rgb(hex) {
  if (!/^#[0-9a-f]{6}$/i.test(hex || '')) return null
  return [1, 3, 5].map(offset => parseInt(hex.slice(offset, offset + 2), 16))
}

// Same weighted RGB distance used by the existing paint report API. This is
// a screen-color comparison, not measured pigment/finish interchangeability.
export function paintDistance(first, second) {
  const a = rgb(first)
  const b = rgb(second)
  if (!a || !b) return Infinity
  return Math.sqrt(2 * (a[0] - b[0]) ** 2 + 4 * (a[1] - b[1]) ** 2 + 3 * (a[2] - b[2]) ** 2)
}

export function similarPaints(source, products, limit = 5) {
  if (!source) return []
  const byBrand = new Map()
  const effect = paintEffect(source)
  if (effect === '辅助用品') return []
  for (const product of products) {
    if (product.brand === source.brand || paintEffect(product) !== effect) continue
    const distance = paintDistance(source.hex_value, product.hex_value)
    // Avoid presenting a visibly distant nearest neighbor as a similar color.
    if (distance > 110) continue
    const previous = byBrand.get(product.brand)
    if (!previous || distance < previous.distance || (distance === previous.distance && product.id < previous.product.id)) byBrand.set(product.brand, { product, distance })
  }
  return [...byBrand.values()].sort((a, b) => a.distance - b.distance || a.product.brand.localeCompare(b.product.brand, 'zh-CN')).slice(0, limit).map(item => item.product)
}
